/**
 * Route definition for /explore
 * Renders the ExploreContainer component with prefetched data.
 *
 * @module routes/explore
 * @category Routes
 */
import { createFileRoute } from '@tanstack/react-router'
import { getAllItems, getListingsByMeta } from '@/lib/api'
import ExploreContainer from '@/containers/Explore'
import { createItemListSchema } from '@/utils/metadata'
import { setPageMetadata } from '@/utils/page-metadata'
import { t } from '@/i18n/translator'

export const Route = createFileRoute('/$locale/explore')({
  loader: async () => {
    // Fetch initial data in parallel
    const [newListed, liveAuctions] = await Promise.all([
      getAllItems(0), // First page of new items
      getListingsByMeta(), // Current live auctions
    ])

    return {
      initialNewListed: newListed,
      initialAuctions: liveAuctions,
    }
  },
  component: Explore,
})

/**
 * Explore page component.
 * Renders the Explore container with prefetched data.
 *
 * @returns {React.ReactElement}
 */
function Explore() {
  const { initialNewListed, initialAuctions } = Route.useLoaderData()

  const pageUrl = 'https://stealthisnft.xyz/explore'

  // Combine both new listed and auctions into a single list for structured data
  const allItems = [
    ...(initialNewListed || []),
    ...(initialAuctions || []),
  ].slice(0, 20)

  const structuredData = allItems.length > 0
    ? createItemListSchema({
        title: t('meta.explore.title'),
        description: t('meta.explore.description'),
        url: pageUrl,
        items: allItems.map(item => ({
          name: item.title || `NFT #${item.tokenId}`,
          description: item.description || '',
          image: item.img_ipfs || item.image || '',
          url: item.route_url || `https://stealthisnft.xyz/item/${item.chain}/${item.contract_address}/${item.tokenId}`,
          price: item.price,
          currency: 'SGB',
        }))
      })
    : undefined

  // Set metadata including dynamic JSON-LD
  setPageMetadata({
    title: t('meta.explore.title'),
    description: t('meta.explore.description'),
    ogImage: '',
    ogType: 'website',
    twitterCard: 'summary_large_image',
    jsonLd: structuredData || null,
  })

  return (
    <ExploreContainer
      initialNewListed={initialNewListed}
      initialAuctions={initialAuctions}
    />
  )
}